import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Mail,
  Users,
  HelpCircle,
  Send,
  MapPin,
  Instagram,
  Youtube,
  Facebook,
  Twitter
} from 'lucide-react';
import Navigation from '../components/Navigation';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form:', formData);
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter signup:', newsletterEmail);
  };

  const contactOptions = [
    {
      icon: Mail,
      title: 'General Inquiries',
      email: 'contact@knowncon.com',
      description: 'Questions about the event, schedule, or tickets'
    },
    {
      icon: Users,
      title: 'Sponsorship & Media',
      email: 'partners@knowncon.com',
      description: 'Partnership opportunities and press inquiries'
    },
    {
      icon: HelpCircle,
      title: 'Accessibility & Support',
      email: 'support@knowncon.com',
      description: 'ADA assistance and attendee support'
    }
  ];

  const socialPlatforms = [
    { icon: Instagram, name: 'Instagram', color: '#E4405F' },
    { icon: Youtube, name: 'YouTube', color: '#FF0000' },
    { icon: Facebook, name: 'Facebook', color: '#1877F2' },
    { icon: Twitter, name: 'X (Twitter)', color: '#000000' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center gap-2 text-sm mb-6 text-gray-300">
            <a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a>
            <span>/</span>
            <span className="text-white font-medium">Contact Us</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Contact <span className="text-[#CFA64B]">Us</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200">
            We're here to help you before, during, and after the conference.
          </p>
        </div>
      </div>

      {/* Section 1: Contact Options Overview */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">How Can We Help?</h2>
            <p className="text-xl text-gray-600">Choose the right department for your inquiry</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {contactOptions.map((option, idx) => {
              const Icon = option.icon;
              return (
                <Card 
                  key={idx}
                  className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                      <Icon className="w-10 h-10 text-[#CFA64B] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">{option.title}</h3>
                    <p className="text-gray-600 mb-4">{option.description}</p>
                    <a 
                      href={`mailto:${option.email}`}
                      className="text-[#CFA64B] hover:text-[#BD954A] font-semibold"
                    >
                      {option.email}
                    </a>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 2: Quick Form */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Send Us a Message</h2>
            <p className="text-xl text-gray-600">We'll get back to you within 24 hours</p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Subject *</label>
                  <select
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="partner">Partnership/Sponsorship</option>
                    <option value="press">Press/Media</option>
                    <option value="support">Support/Assistance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Message *</label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none resize-none"
                    placeholder="How can we help you?"
                  />
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold py-6 text-lg"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
              </form>

              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg hidden" id="success-message">
                <p className="text-green-800 text-center font-medium">
                  Thank you for reaching out. Our team will get back to you soon.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section 3: Event Location */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Event Venue</h2>
            <p className="text-xl text-gray-600">Join us in Los Angeles this August</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-3xl overflow-hidden shadow-2xl">
              <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-24 h-24 text-[#CFA64B] mx-auto mb-4" />
                  <p className="text-2xl font-bold text-[#0B1B3B]">Venue Map</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-3xl font-bold text-[#0B1B3B] mb-6">Los Angeles Convention Center</h3>
              <div className="space-y-4 text-lg text-gray-700">
                <p className="flex items-start gap-3">
                  <MapPin className="w-6 h-6 text-[#CFA64B] flex-shrink-0 mt-1" />
                  <span>1201 S Figueroa Street<br/>Los Angeles, CA 90015</span>
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our venue is fully accessible with multiple entry points, ample parking, and close proximity 
                  to public transportation. Free shuttle service available from partner hotels.
                </p>
              </div>
              <Button className="mt-8 bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-8 py-4">
                <MapPin className="w-5 h-5 mr-2" />
                View on Maps
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Stay Connected Banner */}
      <div className="py-20 bg-gradient-to-r from-[#CFA64B] to-[#d97706]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">
              Follow KNOWN CON for the latest updates and community stories.
            </h2>
          </div>

          {/* Social Icons */}
          <div className="flex justify-center gap-8 mb-12">
            {socialPlatforms.map((platform, idx) => {
              const Icon = platform.icon;
              return (
                <a
                  key={idx}
                  href="#"
                  className="group"
                >
                  <div 
                    className="w-24 h-24 rounded-full flex items-center justify-center hover:scale-110 transition-all shadow-2xl"
                    style={{ backgroundColor: platform.color }}
                  >
                    <Icon className="w-12 h-12 text-white fill-white" strokeWidth={0} fill="currentColor" />
                  </div>
                  <p className="text-center mt-3 text-[#0B1B3B] font-semibold">{platform.name}</p>
                </a>
              );
            })}
          </div>

          {/* Newsletter */}
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full text-lg focus:outline-none focus:ring-2 focus:ring-[#0B1B3B]"
              />
              <Button type="submit" className="bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white font-bold px-10 py-4 rounded-full text-lg">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Attendee Guide</a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">FAQs</a>
            <span className="text-gray-400">|</span>
            <a href="/ada-assistance" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">ADA Assistance</a>
          </div>
        </div>
      </div>

      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;